import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Question, Bookmark, ListChecks, Bell, UserPlus, CaretLeft, CaretRight } from '@phosphor-icons/react';
import CustomPagination from '../../../components/CustomPagination';
import SignInProgressCta from '../../../components/SignInProgressCta';
import { SalaryDisplayToggle } from '../../../components/SalaryDisplayToggle';
import { CompanyGroupingToggle } from '../../../components/CompanyGroupingToggle';
import { ResultsPerPageSelector } from '../../../components/ResultsPerPageSelector';
import type { ViewMode } from '../hooks/useViewMode';
import type { SrpPaginationViewModel, SrpStaticStrings } from '../../../features/srp/SrpViewModel';
import { buildAuthLoginHref, sanitizeReturnTo } from '../../../utils/auth/return-to';
import { trackEvent, trackPagination } from '../../../utils/analytics/analytics';
import { TOOLS_BTN_PRIMARY } from '../../../layouts/tools-page-classes';
import { SRP_HITS_PER_PAGE_OPTIONS, isValidSrpHitsPerPage } from '../../../utils/srp/srp-hits-per-page';
import { SRP_INLINE_SELECT_STATIC_CLASSNAME } from '../../../utils/srp/srp-inline-select-classname';
import { useFormatMessage } from '../../../i18n-review/client/Msg';

export interface SearchPaginationProps {
  viewMode: ViewMode;
  helpTrayOpen: boolean;
  setHelpTrayOpen: (open: boolean) => void;
  pageContextPathname?: string;
  className?: string;
  /** When provided with getPageUrl, render static pagination (same DOM as CustomPagination). */
  staticPagination?: SrpPaginationViewModel;
  getPageUrl?: (page: number) => string;
  /** Pre-generated strings for static SRP (no IntlProvider). When provided with static branch, use instead of useIntl. */
  staticStrings?: SrpStaticStrings;
  /** Locale for analytics tracking on the static branch (no useIntl available there). */
  locale?: string;
  /** Whether the viewer is signed in (static branch only — hides the account sign-up CTA). */
  isAuthenticated?: boolean;
}

const baseItemClasses =
  'min-w-[44px] min-h-[44px] md:min-w-[48px] md:min-h-[48px] px-4 md:px-5 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-medium transition-all duration-200 ease-out active:scale-95 flex items-center justify-center touch-manipulation select-none';

/**
 * Search pagination component - contains pagination controls, keyboard shortcuts button, and feedback button.
 * In static mode (staticPagination + getPageUrl) renders same nav structure with anchor links only.
 */
export const SearchPagination: React.FC<SearchPaginationProps> = ({
  viewMode,
  helpTrayOpen,
  setHelpTrayOpen,
  pageContextPathname,
  className = '',
  staticPagination,
  getPageUrl,
  staticStrings,
  locale,
  isAuthenticated = false,
}) => {
  if (viewMode === 'map') {
    return null;
  }

  const useStatic = staticPagination != null && getPageUrl != null && staticStrings != null;

  if (useStatic && staticPagination && getPageUrl && staticStrings) {
    const { currentPage, nbPages, prevUrl, nextUrl, hitsPerPage } = staticPagination;
    const staticPerPageValue = isValidSrpHitsPerPage(hitsPerPage) ? hitsPerPage : SRP_HITS_PER_PAGE_OPTIONS[0];
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(0, currentPage - half);
    let end = start + (windowSize - 1);
    if (end > nbPages - 1) {
      end = nbPages - 1;
      start = Math.max(0, end - (windowSize - 1));
    }
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    const isFirstPage = currentPage <= 0;
    const isLastPage = currentPage >= nbPages - 1;
    const ariaLabel = staticStrings?.paginationAriaLabel ?? 'Job search results pagination';
    const firstLabel = staticStrings?.paginationFirst ?? 'First';
    const prevLabel = staticStrings?.paginationPrevious ?? 'Previous';
    const nextLabel = staticStrings?.paginationNext ?? 'Next';
    const lastLabel = staticStrings?.paginationLast ?? 'Last';
    const signInHref = buildAuthLoginHref(sanitizeReturnTo(pageContextPathname));
    const trackSignInCtaClick = () =>
      trackEvent({
        name: 'auth_interaction',
        params: { action: 'sign_in_click', surface: 'srp_pagination_signin_cta', locale },
      });

    return (
      <nav
        id="pagination"
        className={`pagination w-full pt-0 lg:pt-4 pb-4 items-center justify-center ${className}`}
        aria-label={ariaLabel}
      >
        <div className="flex justify-center py-2 md:py-3">
          <div className="flex flex-col items-center gap-3 w-full max-w-4xl px-4">
            <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-3 w-full">
              <div className="flex justify-center items-center gap-2 flex-wrap">
              {!isFirstPage && (
                <>
                  <a
                    href={getPageUrl(0)}
                    onClick={() => trackPagination({ pageNumber: 1, locale })}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    {firstLabel}
                  </a>
                  <a
                    href={prevUrl ?? getPageUrl(currentPage - 1)}
                    onClick={() => trackPagination({ pageNumber: currentPage, locale })}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    <div className="flex items-center gap-1.5">
                      <CaretLeft className="w-4 h-4 text-[#3D3529] dark:text-[#C4A882]" />
                      {prevLabel}
                    </div>
                  </a>
                </>
              )}
              {pages.map((page) => {
                const label = page + 1;
                const isActive = currentPage === page;
                return (
                  <a
                    key={page}
                    href={getPageUrl(page)}
                    onClick={() => trackPagination({ pageNumber: label, locale })}
                    aria-current={isActive ? 'page' : undefined}
                    className={`${baseItemClasses} ${isActive
                      ? 'bg-[#3B5E45] dark:bg-[#1A2E22] text-white dark:text-[#98CFA8] font-semibold shadow-md dark:shadow-none border border-[#2A4533] dark:border-[#4A3520]'
                      : 'bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none'
                    }`}
                  >
                    {label}
                  </a>
                );
              })}
              {!isLastPage && (
                <>
                  <a
                    href={nextUrl ?? getPageUrl(currentPage + 1)}
                    onClick={() => trackPagination({ pageNumber: currentPage + 2, locale })}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    <div className="flex items-center gap-1.5">
                      {nextLabel}
                      <CaretRight className="w-4 h-4 text-[#3D3529] dark:text-[#C4A882]" />
                    </div>
                  </a>
                  <a
                    href={getPageUrl(nbPages - 1)}
                    onClick={() => trackPagination({ pageNumber: nbPages, locale })}
                    className={`${baseItemClasses} bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] hover:bg-[#F2EADF] dark:hover:bg-[#342415] border border-[#DDD5C8] dark:border-[#4A3520] shadow-sm dark:shadow-none`}
                  >
                    {lastLabel}
                  </a>
                </>
              )}
              </div>
              {/* Match interactive layout: keyboard hint placeholder in-row + sign-in CTA so page height doesn't jump on swap */}
              <div className="hidden md:flex items-center shrink-0 justify-center">
                <span
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#7A6E5F] dark:text-[#B8A48C]"
                  aria-hidden
                >
                  <Question className="w-6 h-6" />
                </span>
              </div>
            </div>
            <select
              className={SRP_INLINE_SELECT_STATIC_CLASSNAME}
              value={staticPerPageValue}
              disabled
              aria-label={staticStrings.resultsPerPageAria}
              tabIndex={-1}
            >
              {SRP_HITS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {staticStrings.resultsPerPageOptionLabelTemplate.replace('{count}', String(option))}
                </option>
              ))}
            </select>
            {!isAuthenticated && (
              <div className="mt-4 px-4">
                <div className="flex flex-col gap-4 rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-jc-surface p-4 shadow-sm dark:shadow-none sm:p-6 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-jc-page text-jc-cta dark:text-[#98CFA8]">
                        <UserPlus className="h-4 w-4" />
                      </div>
                      <h2 className="text-base font-semibold text-jc-ink sm:text-lg">
                        {staticStrings.signInCta.title}
                      </h2>
                    </div>
                    <ul className="mt-3 flex flex-col gap-1.5 sm:mt-4">
                      <li className="flex items-center gap-2 text-sm text-jc-ink-secondary">
                        <Bookmark className="h-4 w-4 shrink-0 text-jc-muted" />
                        {staticStrings.signInCta.benefit1}
                      </li>
                      <li className="flex items-center gap-2 text-sm text-jc-ink-secondary">
                        <ListChecks className="h-4 w-4 shrink-0 text-jc-muted" />
                        {staticStrings.signInCta.benefit2}
                      </li>
                      <li className="flex items-center gap-2 text-sm text-jc-ink-secondary">
                        <Bell className="h-4 w-4 shrink-0 text-jc-muted" />
                        {staticStrings.signInCta.benefit3}
                      </li>
                    </ul>
                  </div>
                  <div className="flex w-full flex-col items-stretch gap-2 lg:w-auto lg:items-end">
                    <a href={signInHref} onClick={trackSignInCtaClick} className={`${TOOLS_BTN_PRIMARY} text-center`}>
                      {staticStrings.signInCta.button}
                    </a>
                    <a
                      href={signInHref}
                      onClick={trackSignInCtaClick}
                      className="text-center text-xs text-jc-muted underline underline-offset-2 hover:text-jc-ink-secondary sm:text-sm"
                    >
                      {staticStrings.signInCta.secondaryText}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    );
  }

  const intl = useIntl();
  const formatMessage = useFormatMessage();
  return (
    <>
      <nav
        id="pagination"
        className={`pagination w-full pt-0 lg:pt-4 pb-4 items-center justify-center ${className}`}
        aria-label={formatMessage({
          id: 'pagination.ariaLabel',
          defaultMessage: 'Job search results pagination',
          description: 'Accessibility label for pagination navigation controls',
        })}
      >
        <CustomPagination
          trailingSlot={
            <button
              type="button"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F2EADF] dark:bg-[#2A1D0F] hover:bg-[#C8DDCE] dark:hover:bg-[#342415] text-[#3D3529] dark:text-[#C4A882] transition-all duration-200 shadow dark:shadow-none"
              aria-label={formatMessage({
                id: 'pagination.keyboardShortcuts',
                defaultMessage: 'Keyboard shortcuts',
                description: 'Button aria-label to open keyboard shortcuts help on search results',
              })}
              onClick={() => setHelpTrayOpen(true)}
              tabIndex={0}
            >
              <Question className="w-6 h-6" aria-hidden />
            </button>
          }
        />
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
          <ResultsPerPageSelector />
          <span className="h-4 w-px bg-[#DDD5C8] dark:bg-[#4A3520] shrink-0" aria-hidden="true" />
          <SalaryDisplayToggle />
          <span className="h-4 w-px bg-[#DDD5C8] dark:bg-[#4A3520] shrink-0" aria-hidden="true" />
          <CompanyGroupingToggle />
        </div>
        <SignInProgressCta className="mt-4 px-4" />
      </nav>
    </>
  );
};
